/**
 * Per-agent web search site lists — the editor's half of the rules.
 *
 * Mirrors backend/shared/services/ai/web_search_sites.py (the authority — the
 * server validates every save the same way): a site is a bare lower-case host,
 * no scheme / path / port, a leading "www." dropped, subdomains always
 * included. Stored as handler_config.web_search.
 */

export const MAX_SITES_PER_LIST = 50;

export interface WebSearchSites {
  blocked_domains: string[];
  allowed_domains: string[];
}

export type SiteError = 'invalid' | 'wildcard' | 'duplicate' | 'tooMany' | 'nothingLeft';

const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;

/** A pasted site, URL or host -> the stored host, or the reason it is refused. */
export function normalizeSite(raw: string): { site: string } | { error: SiteError } {
  const text = raw.trim().toLowerCase();
  if (!text) return { error: 'invalid' };
  if (text.includes('*')) return { error: 'wildcard' };
  let host: string;
  try {
    const withScheme = text.includes('//') ? (text.startsWith('//') ? `http:${text}` : text) : `http://${text}`;
    host = new URL(withScheme).hostname;
  } catch {
    return { error: 'invalid' };
  }
  host = host.replace(/\.+$/, '');
  if (host.startsWith('www.')) host = host.slice(4);
  const labels = host.split('.');
  if (host.length > 253 || labels.length < 2 || !labels.every(l => LABEL.test(l)) || /^\d+$/.test(labels[labels.length - 1])) {
    return { error: 'invalid' };
  }
  return { site: host };
}

/** `host` is `domain` or one of its subdomains. */
export function covers(domain: string, host: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** The allowed sites left once blocked ones are taken out (what is searched). */
export function effectiveAllowed(sites: WebSearchSites): string[] {
  return sites.allowed_domains.filter(a => !sites.blocked_domains.some(b => covers(b, a)));
}

/** Add one entry to one list, or say why not. Mirrors the server's save rules. */
export function addSite(
  sites: WebSearchSites,
  list: keyof WebSearchSites,
  raw: string,
): { sites: WebSearchSites } | { error: SiteError; site?: string } {
  const parsed = normalizeSite(raw);
  if ('error' in parsed) return { error: parsed.error };
  const current = sites[list];
  if (current.includes(parsed.site)) return { error: 'duplicate', site: parsed.site };
  if (current.length >= MAX_SITES_PER_LIST) return { error: 'tooMany' };
  const next = { ...sites, [list]: [...current, parsed.site] };
  if (next.allowed_domains.length > 0 && effectiveAllowed(next).length === 0) {
    return { error: 'nothingLeft' };
  }
  return { sites: next };
}

const NO_SITES: string[] = [];

/** The stored lists, or empty ones. Never mutated — edits build new arrays. */
export function readSites(webSearch: Partial<WebSearchSites> | undefined | null): WebSearchSites {
  return {
    blocked_domains: webSearch?.blocked_domains ?? NO_SITES,
    allowed_domains: webSearch?.allowed_domains ?? NO_SITES,
  };
}
